import React, {
  useState, useMemo, useRef, useEffect, useCallback,
} from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button, Form } from 'react-bootstrap';
import DOMPurify from 'dompurify';

const Delta = Quill.import('delta');

const TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

// Whitelist only tags Quill can handle — strips email signatures, logos, embedded objects, etc.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'b', 'i', 'u', 'strong', 'em', 's', 'strike',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'blockquote', 'pre', 'code',
    'span', 'div',
    'sub', 'sup',
  ],
  ALLOWED_ATTR: ['href', 'target'],
  ALLOW_DATA_ATTR: false,
};

function sanitize(html) {
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

/**
 * Flatten complex pasted HTML into simple Quill-safe markup.
 * Converts nested divs to <p>, collapses empty nodes, strips style attrs.
 */
function flattenForQuill(html) {
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
  const doc = new DOMParser().parseFromString(clean, 'text/html');

  // Replace divs with p tags (Quill prefers p for blocks)
  doc.querySelectorAll('div').forEach((div) => {
    const p = doc.createElement('p');
    p.innerHTML = div.innerHTML;
    div.replaceWith(p);
  });

  // Remove empty paragraphs/spans that produce no visible content
  doc.querySelectorAll('p, span').forEach((el) => {
    if (!el.textContent.trim() && !el.querySelector('br, img')) {
      el.remove();
    }
  });

  return doc.body.innerHTML;
}

function HtmlEditor({
  value, onChange, placeholder, readOnly, style,
}) {
  const [showSource, setShowSource] = useState(false);
  const quillRef = useRef(null);

  // Intercept paste BEFORE Quill processes it — sanitize & flatten clipboard HTML
  const handlePaste = useCallback((e) => {
    const clipboardHtml = e.clipboardData?.getData('text/html');
    if (!clipboardHtml) return; // plain text paste — let Quill handle it

    e.preventDefault();
    e.stopPropagation();

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const idx = range?.index || 0;

    // Try pasting flattened HTML; fall back to plain text if Quill chokes
    try {
      const flat = flattenForQuill(clipboardHtml);
      editor.clipboard.dangerouslyPasteHTML(idx, flat, 'user');
    } catch {
      // Ultimate fallback: insert as plain text
      const plainText = e.clipboardData?.getData('text/plain') || '';
      if (plainText) {
        editor.insertText(idx, plainText, 'user');
      }
    }
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return undefined;
    const { root } = editor;
    root.addEventListener('paste', handlePaste, true);
    return () => root.removeEventListener('paste', handlePaste, true);
  }, [handlePaste, showSource]);

  const modules = useMemo(() => ({
    toolbar: readOnly ? false : TOOLBAR_OPTIONS,
    clipboard: {
      matchers: [
        ['img', () => new Delta()],
      ],
    },
  }), [readOnly]);

  const handleChange = (html) => {
    if (onChange) {
      // Don't sanitize on every keystroke — it normalizes HTML slightly,
      // causing Quill to see a different value and fire onChange again (infinite loop).
      // Paste is already sanitized via the paste interceptor above.
      onChange(html);
    }
  };

  const handleSourceChange = (e) => {
    if (onChange) {
      onChange(sanitize(e.target.value));
    }
  };

  return (
    <div>
      {!readOnly && (
        <div className="d-flex justify-content-end mb-1">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted"
            onClick={() => setShowSource(!showSource)}
          >
            <i className={`fa fa-${showSource ? 'eye' : 'code'} me-1`} />
            {showSource ? 'Visual' : 'HTML Source'}
          </Button>
        </div>
      )}

      {showSource ? (
        <Form.Control
          as="textarea"
          rows={10}
          value={value || ''}
          onChange={handleSourceChange}
          placeholder={placeholder}
          style={{ fontFamily: 'monospace', fontSize: '0.85em', ...style }}
        />
      ) : (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={handleChange}
          modules={modules}
          placeholder={placeholder}
          readOnly={readOnly}
          style={style}
        />
      )}
    </div>
  );
}

export default HtmlEditor;
